import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import StarRating from "../../components/StarRating";
import axios from "axios";

const LandingPage = () => {
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchQuery, setSearchQuery] = useState("");
    const [finalSearchQuery, setFinalSearchQuery] = useState("");
    const [categoryFilter, setCategoryFilter] = useState(0);
    const [manufacturerFilter, setManufacturerFilter] = useState("All");
    const [sortBy, setSortBy] = useState("reviewAverage");
    const [order, setOrder] = useState("desc");
    const [limit] = useState(9);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const observer = useRef();

    useEffect(() => {
        const fetchProducts = async () => {
            try {
                setLoading(true);
                const response = await axios.get("/api/products", {
                    params: {
                        searchTerm: finalSearchQuery,
                        filters: {
                            category: categoryFilter === 0 ? [] : [categoryFilter],
                            manufacturer: manufacturerFilter === "All" ? [] : [manufacturerFilter],
                        },
                        sortBy,
                        order,
                        limit,
                        page,
                    },
                });
                if (page > 1) {
                    setProducts((prevProducts) => [...prevProducts, ...response.data.products]);
                } else {
                    setProducts(response.data.products);
                }
                setHasMore(response.data.hasMore);
            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        fetchProducts();

    }, [finalSearchQuery, categoryFilter, manufacturerFilter, sortBy, order, limit, page]);

    const handleSearchChange = (e) => {
        setSearchQuery(e.target.value);
        if (!e.target.value.length) {
            setFinalSearchQuery(e.target.value);
            setProducts([]);
            setPage(1);
        }
    };

    const handleSearchKeyDown = (e) => {
        if (e.key === "Enter") {
            setFinalSearchQuery(e.target.value);
            setProducts([]);
            setPage(1);
        }
    };

    const handleCategoryChange = (e) => {
        setCategoryFilter(e.target.value);
        setProducts([]);
        setPage(1);
    };

    const handleManufacturerChange = (e) => {
        setManufacturerFilter(e.target.value);
        setProducts([]);
        setPage(1);
    };

    const handleSortChange = (e) => {
        const [sortField, sortOrder] = e.target.value.split(",");
        setSortBy(sortField);
        setOrder(sortOrder);
        setProducts([]);
        setPage(1);
    };

    const lastProductElementRef = useRef(null);
    useEffect(() => {
        const observerCallback = (entries) => {
            if (entries[0].isIntersecting && hasMore && !loading) {
                setPage((prevPage) => prevPage + 1);
            }
        };

        const currentObserver = observer.current;
        if (currentObserver) {
            currentObserver.disconnect();
        }

        observer.current = new IntersectionObserver(observerCallback);
        if (lastProductElementRef.current) {
            observer.current.observe(lastProductElementRef.current);
        }

        return () => {
            if (currentObserver) {
                currentObserver.disconnect();
            }
        };
    }, [loading, hasMore]);

    return (
        <div className="container mx-auto p-4">
            <div className="my-4 flex flex-col sm:flex-row justify-between items-center space-y-4 sm:space-y-0">
                <input
                    type="text"
                    placeholder="Search by title..."
                    className="border border-gray-300 p-2 rounded w-full sm:w-1/3"
                    value={searchQuery}
                    onChange={handleSearchChange}
                    onKeyDown={handleSearchKeyDown}
                />
                <select
                    className="border border-gray-300 p-2 rounded w-full sm:w-1/4"
                    value={categoryFilter}
                    onChange={handleCategoryChange}
                >
                    <option value="0">All Categories</option>
                    <option value="1">Computers</option>
                    <option value="2">Accessories</option>
                </select>
                <select
                    className="border border-gray-300 p-2 rounded w-full sm:w-1/4"
                    value={manufacturerFilter}
                    onChange={handleManufacturerChange}
                >
                    <option value="All">All Manufacturers</option>
                    <option value="Apple">Apple</option>
                    <option value="Dell">Dell</option>
                    <option value="Acer">Acer</option>
                    <option value="Asus">Asus</option>
                    <option value="Lenovo">Lenovo</option>
                    <option value="HP">HP</option>
                </select>
                <select
                    className="border border-gray-300 p-2 rounded w-full sm:w-1/4"
                    onChange={handleSortChange}
                >
                    <option value="reviewAverage,desc">Sort by Rating (High to Low)</option>
                    <option value="reviewCount,desc">Sort by Review Count (High to Low)</option>
                    <option value="price,desc">Sort by Price (High to Low)</option>
                    <option value="price,asc">Sort by Price (Low to High)</option>
                </select>
            </div>

            {loading && <p>Loading...</p>}
            {error && <p className="text-red-500">{error}</p>}

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {products.map((product, index) => (
                    <Link
                        key={product._id}
                        to={`/product/${product._id}`}
                        className="border rounded-lg shadow-lg overflow-hidden hover:shadow-xl transform hover:scale-105 transition duration-200"
                        ref={index === products.length - 1 ? lastProductElementRef : null}
                    >
                        <img
                            src={product.cover}
                            alt={product.title}
                            className="w-full h-48 object-cover"
                        />
                        <div className="p-4">
                            <h2 className="text-lg font-semibold">{product.title}</h2>
                            <p className="text-red-800 font-bold">${product.price.toFixed(2)}</p>
                            <p className="text-green-600 font-bold">Stock: {product.stock}</p>
                            <StarRating rating={product.reviewAverage} reviews={product.reviewCount || 0} showCount={true} />
                        </div>
                    </Link>
                ))}
            </div>
        </div>
    );
};

export default LandingPage;

